import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import AuthShell from "./AuthShell";
import GoogleButton from "@/components/auth/GoogleButton";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", form);
      track("Logged In", { method: "password" });
      setUser(data);
      const dest = location.state?.from || "/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Log in"
      subtitle={<>Welcome <span className="italic text-aurora">back.</span></>}
      footer={
        <span className="text-xs text-neutral-500 tracking-[0.18em] uppercase">
          New here?{" "}
          <Link to="/signup" className="text-gold hover:text-gold" data-testid="login-to-signup">
            Create account
          </Link>
        </span>
      }
    >
      <GoogleButton label="Log in with Google" testid="login-google" />

      <div className="relative my-6 sm:my-8 text-center">
        <div className="absolute inset-x-0 top-1/2 h-px bg-neutral-100" />
        <span className="relative bg-white px-3 text-[10px] uppercase tracking-[0.28em] text-neutral-400">
          or with email
        </span>
      </div>

      <form onSubmit={submit} data-testid="login-form" className="space-y-6">
        <div>
          <label className="overline block mb-2" htmlFor="li-email">Email</label>
          <input
            id="li-email"
            data-testid="login-email"
            required
            type="email"
            value={form.email}
            onChange={update("email")}
            className="editorial-input"
            placeholder="jane@company.com"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="overline block mb-2" htmlFor="li-password">Password</label>
          <input
            id="li-password"
            data-testid="login-password"
            required
            type="password"
            value={form.password}
            onChange={update("password")}
            className="editorial-input"
            placeholder="Your password"
            autoComplete="current-password"
          />
        </div>

        {error && (
          <p data-testid="login-error" className="text-sm text-rose-300">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="btn-aurora w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="login-submit"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Signing in…
            </>
          ) : (
            <>
              Log in <ArrowUpRight size={16} />
            </>
          )}
        </button>
      </form>
    </AuthShell>
  );
}
